import { useCallback, useEffect, useRef, useState } from "react";
import { Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import { EditorElement } from "@smart/itops-serialisation-basic";

type InlineEditInputProps = {
  element: EditorElement<"placeholder">;
  value: string;
  defaultValue: string;
  onEditEnd: () => void;
};

export const InlineEditInput = ({
  element,
  value,
  defaultValue,
  onEditEnd,
}: InlineEditInputProps) => {
  const editor = useSlate();
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const [draftText, setDraftText] = useState(value);
  const committedRef = useRef(false);

  const updateWidth = useCallback(() => {
    if (measureRef.current) {
      setInputWidth(measureRef.current.scrollWidth);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    inputRef.current?.focus();
  }, [updateWidth]);

  useEffect(() => {
    updateWidth();
  }, [draftText, updateWidth]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;

    const newText = draftText.trim();
    const committedText = newText || defaultValue;
    if (committedText !== value) {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, { displayText: committedText }, { at: path });
    }
    onEditEnd();
  };

  const cancel = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    onEditEnd();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={draftText}
        onChange={(e) => setDraftText(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-transparent outline-none p-0 m-0 border-0 text-inherit font-inherit max-w-full"
        style={{ width: inputWidth ?? "auto" }}
      />
      {/* For measuring actual rendered text width */}
      <span
        ref={measureRef}
        className="absolute invisible overflow-hidden whitespace-pre h-0 font-inherit text-inherit"
        aria-hidden="true"
      >
        {draftText || "\u00A0"}
      </span>
    </>
  );
};
